import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, X, Image, Loader2, Download, Wand2, ShoppingBag, Check, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

export default function MerchantAIStudioLab() {
  const [merchantId, setMerchantId] = useState('');
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const PAGE_SIZE = 50;
  const [hasMoreServerPages, setHasMoreServerPages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Single product selection
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // AI Studio states
  const [aiMode, setAiMode] = useState('collage');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  
  const brandId = useMemo(() => {
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    const tryKeys = ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id'];
    for (const k of tryKeys) {
      try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {}
    }
    const tryJson = ['selectedBrand', 'currentBrand', 'activeBrand'];
    for (const k of tryJson) {
      try {
        const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (raw) { const obj = JSON.parse(raw); if (obj?.id) return obj.id; }
      } catch(_) {}
    }
    return '';
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch((search || '').trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Load Merchant Center products
  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);
        let mid = '';
        
        if (brandId) {
          const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            let found = null;
            if (Array.isArray(data)) {
              found = data.find(x => (x?.platform === 'MERCHANT_CENTER') || (x?.platform === 'merchant_center')) || null;
            } else if (data && typeof data === 'object') {
              const mc = data['MERCHANT_CENTER'] || data['merchant_center'] || null;
              if (mc) found = mc;
            }
            mid = found?.customerId || found?.merchantId || '';
          }
        }
        
        if (mid) setMerchantId(String(mid));

        // Fetch products
        if (mid && brandId) {
          const limit = PAGE_SIZE;
          const offset = 0;
          const pr = await fetch(
            `https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?merchantId=${encodeURIComponent(mid)}&limit=${limit}&offset=${offset}`,
            {
              headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
              credentials: 'include'
            }
          );
          const prText = await pr.text();
          let prJson;
          try { prJson = JSON.parse(prText); } catch { prJson = { raw: prText }; }
          if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
          
          const rows = Array.isArray(prJson?.data) ? prJson.data : [];
          const mapped = rows.map((r) => ({
            offerId: r.offer_id,
            productId: r.product_id,
            title: r.title,
            brand: r.brand,
            availability: r.availability,
            price: { value: r.price_value, currency: r.price_currency },
            salePrice: r.sale_price_value ? { value: r.sale_price_value, currency: r.sale_price_currency } : null,
            imageLink: r.image_link,
            additionalImages: Array.isArray(r.additional_image_links) ? r.additional_image_links : [],
            category: r.google_product_category,
            link: r.link,
          }));
          setAllProducts(mapped);
          setPageIndex(1);
          setHasMoreServerPages(rows.length === limit);
        }
      } catch (e) {
        setErrorMsg(e?.message || 'Load error');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [brandId]);

  // Search effect
  useEffect(() => {
    const run = async () => {
      try {
        if (!brandId || !merchantId) return;
        setIsLoadingMore(true);
        setErrorMsg('');
        setPageIndex(1);
        setHasMoreServerPages(true);

        const qs = new URLSearchParams();
        qs.set('merchantId', merchantId);
        qs.set('limit', String(PAGE_SIZE));
        qs.set('offset', '0');
        if (debouncedSearch) qs.set('q', debouncedSearch);

        const pr = await fetch(`https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?${qs.toString()}`, {
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        const prText = await pr.text();
        let prJson;
        try { prJson = JSON.parse(prText); } catch { prJson = { raw: prText }; }
        if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
        
        const rows = Array.isArray(prJson?.data) ? prJson.data : [];
        const mapped = rows.map((r) => ({
          offerId: r.offer_id,
          productId: r.product_id,
          title: r.title,
          brand: r.brand,
          availability: r.availability,
          price: { value: r.price_value, currency: r.price_currency },
          salePrice: r.sale_price_value ? { value: r.sale_price_value, currency: r.sale_price_currency } : null,
          imageLink: r.image_link,
          additionalImages: Array.isArray(r.additional_image_links) ? r.additional_image_links : [],
          category: r.google_product_category,
          link: r.link,
        }));
        setAllProducts(mapped);
        setHasMoreServerPages(rows.length === PAGE_SIZE);
      } catch (e) {
        setErrorMsg(e?.message || 'Search error');
      } finally {
        setIsLoadingMore(false);
      }
    };
    run();
  }, [debouncedSearch, merchantId, brandId]);

  const filteredProducts = useMemo(() => {
    let arr = allProducts;
    const q = (debouncedSearch || '').trim().toLowerCase();
    if (q) {
      arr = arr.filter(p =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.offerId || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [allProducts, debouncedSearch]);

  const visibleProducts = useMemo(() => {
    const start = (pageIndex - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredProducts.slice(start, end);
  }, [filteredProducts, pageIndex]);

  // Get all images for selected product
  const selectedProductImages = useMemo(() => {
    if (!selectedProduct) return [];
    return [selectedProduct.imageLink, ...(selectedProduct.additionalImages || [])].filter(Boolean);
  }, [selectedProduct]);

  // Fetch images via backend proxy to avoid CORS
  const fetchImagesAsBase64 = async (urls) => {
    try {
      const resp = await fetch('https://api.glowytics.com/api/ai/image-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({ urls })
      });
      
      if (!resp.ok) {
        throw new Error('Proxy request failed');
      }
      
      const data = await resp.json();
      return data.results
        .filter(r => r.success && r.base64)
        .map(r => r.base64);
    } catch (err) {
      console.error('Image proxy error:', err);
      return [];
    }
  };

  // Format price
  const formatPrice = (product) => {
    const p = product?.salePrice || product?.price;
    if (!p?.value) return '';
    return `${Number(p.value).toLocaleString('tr-TR')} ${p.currency || 'TRY'}`;
  };

  // AI Generation
  const generateWithAI = async () => {
    if (!selectedProduct) {
      toast.error('Please select a product first');
      return;
    }
    
    setAiGenerating(true);
    setAiResult(null);
    
    try {
      // Fetch all product images via backend proxy
      toast.loading('Loading product images...', { id: 'loading-images' });
      const base64Images = await fetchImagesAsBase64(selectedProductImages);
      toast.dismiss('loading-images');
      
      if (base64Images.length === 0) {
        throw new Error('Could not load product images');
      }

      const price = formatPrice(selectedProduct);
      const productTitle = selectedProduct.title || 'Product';
      
      // Build prompt based on mode
      const modePrompts = {
        collage: `Create a beautiful product collage ad creative using these ${base64Images.length} product images. 
Requirements:
- Arrange all product images in an aesthetically pleasing grid or dynamic layout
- Add a modern, elegant frame or border design
- Display the price "${price}" prominently in a stylish badge or banner
- Clean white or soft gradient background
- Professional e-commerce advertisement style
- Make it look premium and eye-catching for social media ads
Product: ${productTitle}`,
        
        promo: `Create a promotional sale advertisement using these product images.
Requirements:
- Feature the product images prominently
- Add dynamic sale/discount visual elements (ribbons, badges, starburst)
- Display price "${price}" in a bold, attention-grabbing way
- Add subtle frame or border design
- Vibrant, energetic colors that convey urgency
- Perfect for Instagram/Facebook ads
Product: ${productTitle}`,
        
        premium: `Create a luxury, high-end product advertisement using these images.
Requirements:
- Elegant, sophisticated arrangement of product images
- Premium frame with subtle gold or silver accents
- Display price "${price}" in an understated, classy manner
- Dark or neutral luxury background
- Magazine-quality aesthetic
- High-end brand feel
Product: ${productTitle}`,
        
        minimal: `Create a minimalist product advertisement using these images.
Requirements:
- Clean, simple layout with plenty of white space
- Product images arranged with geometric precision
- Price "${price}" displayed in clean, modern typography
- Thin, elegant frame or no frame
- Ultra-clean, Scandinavian design aesthetic
- Perfect for modern e-commerce
Product: ${productTitle}`
      };
      
      const prompt = modePrompts[aiMode] || modePrompts.collage;
      
      // Call Gemini API
      const resp = await fetch('https://api.glowytics.com/api/ai/gemini/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          model: 'gemini-2.0-flash-exp-image-generation',
          temperature: 0.8,
          messages: [{
            role: 'user',
            content: prompt,
            images: base64Images
          }]
        })
      });
      
      const result = await resp.json();
      
      if (!resp.ok) {
        throw new Error(result?.error || 'AI generation failed');
      }
      
      // Check for generated image
      if (result?.image) {
        setAiResult({
          type: 'image',
          data: result.image,
          message: result.message || ''
        });
        toast.success('Creative generated successfully!');
      } else {
        setAiResult({
          type: 'text',
          message: result.message || 'No image generated'
        });
      }
    } catch (err) {
      console.error('AI generation error:', err);
      toast.error(err.message || 'An error occurred');
    } finally {
      setAiGenerating(false);
    }
  };

  // Download generated image
  const downloadImage = () => {
    if (!aiResult?.data) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${aiResult.data}`;
    link.download = `product-creative-${aiMode}-${Date.now()}.png`;
    link.click();
    toast.success('Downloaded!');
  };

  const modes = [
    { id: 'collage', label: 'Collage', desc: 'Multi-image grid layout', icon: '🎨' },
    { id: 'promo', label: 'Promo', desc: 'Sale/discount style', icon: '🏷️' },
    { id: 'premium', label: 'Premium', desc: 'Luxury aesthetic', icon: '✨' },
    { id: 'minimal', label: 'Minimal', desc: 'Clean & simple', icon: '◻️' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Merchant AI Studio</h1>
              <p className="text-gray-500 text-sm">Create AI-powered product creatives from your catalog</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left: Product Selection */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Search */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search products..."
                    className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                  <span className="text-xs text-gray-500 whitespace-nowrap">{filteredProducts.length} products</span>
                </div>
              </div>
              
              {/* Products List */}
              <div className="p-4 max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                  </div>
                ) : errorMsg ? (
                  <div className="text-center py-12 text-red-500 text-sm">{errorMsg}</div>
                ) : visibleProducts.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 text-sm">No products found</div>
                ) : (
                  <div className="space-y-2">
                    {visibleProducts.map((product) => {
                      const allImages = [product.imageLink, ...(product.additionalImages || [])].filter(Boolean);
                      const isSelected = selectedProduct?.offerId === product.offerId;
                      return (
                        <div
                          key={product.offerId}
                          onClick={() => {
                            setSelectedProduct(isSelected ? null : product);
                            setAiResult(null);
                          }}
                          className={`flex items-center gap-4 p-3 rounded-xl border cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-indigo-500 bg-indigo-50/50 shadow-sm' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {/* Selection Checkbox */}
                          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected 
                              ? 'bg-indigo-600' 
                              : 'border border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          
                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{product.title}</p>
                            <p className="text-xs text-indigo-600 font-medium mt-0.5">
                              {formatPrice(product) || '-'}
                            </p>
                          </div>
                          
                          {/* All Images */}
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {allImages.length > 0 ? (
                              allImages.slice(0, 6).map((img, idx) => (
                                <div key={idx} className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                                  <img 
                                    src={img} 
                                    alt={`${product.title} ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ))
                            ) : (
                              <div className="w-10 h-10 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                                <Image className="w-4 h-4 text-gray-300" />
                              </div>
                            )}
                            {allImages.length > 6 && (
                              <div className="w-10 h-10 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center text-xs text-gray-500 font-medium">
                                +{allImages.length - 6}
                              </div>
                            )}
                          </div>
                          
                          <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-colors ${isSelected ? 'text-indigo-500' : 'text-gray-300'}`} />
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Pagination */}
                {filteredProducts.length > PAGE_SIZE && (
                  <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                    <button
                      onClick={() => setPageIndex(p => Math.max(1, p - 1))}
                      disabled={pageIndex <= 1}
                      className="px-3 py-1.5 text-xs border rounded-lg disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-500">Page {pageIndex}</span>
                    <button
                      onClick={() => setPageIndex(p => p + 1)}
                      disabled={(pageIndex * PAGE_SIZE) >= filteredProducts.length && !hasMoreServerPages}
                      className="px-3 py-1.5 text-xs border rounded-lg disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Right: AI Studio Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden sticky top-6">
              <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  <h2 className="font-semibold text-gray-900">AI Creative Studio</h2>
                </div>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Selected Product Preview */}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Selected Product</label>
                  {!selectedProduct ? (
                    <div className="mt-2 p-4 border-2 border-dashed rounded-xl text-center">
                      <p className="text-sm text-gray-400">Select a product from the list</p>
                    </div>
                  ) : (
                    <div className="mt-2 p-3 bg-gray-50 rounded-xl">
                      <p className="text-sm font-medium text-gray-800 line-clamp-2">{selectedProduct.title}</p>
                      <p className="text-xs text-indigo-600 font-medium mt-1">{formatPrice(selectedProduct)}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {selectedProductImages.map((img, idx) => (
                          <div key={idx} className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200">
                            <img src={img} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-2">{selectedProductImages.length} images will be used</p>
                    </div>
                  )}
                </div>
                
                {/* Mode Selection */}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Creative Style</label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {modes.map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => setAiMode(mode.id)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          aiMode === mode.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-lg">{mode.icon}</span>
                        <p className="text-xs font-medium text-gray-800 mt-1">{mode.label}</p>
                        <p className="text-[10px] text-gray-500">{mode.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Generate Button */}
                <button
                  onClick={generateWithAI}
                  disabled={aiGenerating || !selectedProduct}
                  className="w-full py-3 px-4 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {aiGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Generate Creative
                    </>
                  )}
                </button>
                
                {/* Result */}
                {aiResult && (
                  <div className="mt-4">
                    {aiResult.type === 'image' && aiResult.data ? (
                      <div className="space-y-3">
                        <div className="rounded-xl overflow-hidden border">
                          <img 
                            src={`data:image/png;base64,${aiResult.data}`}
                            alt="AI Generated"
                            className="w-full"
                          />
                        </div>
                        <button
                          onClick={downloadImage}
                          className="w-full py-2 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </div>
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-xl text-sm text-gray-600">
                        {aiResult.message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
